import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import apiService from '../../service/ApiService';
import InitiationShell from './InitiationShell';
import './PocTypePage.scss';

type PocType = 'EXTERNAL_SUPPLIER' | 'INTERNAL_DEVELOPMENT';
type InitiationSource = 'INTERNAL' | 'EXTERNAL';

type LocationState = {
  pocId?: string;
  pocType?: PocType;
};

const BUSINESS_DOMAINS = [
  'Cleaning',
  'Food',
  'Technical Services',
  'Workplace',
  'P&C',
  'IT&D',
  'Finance',
  'Legal & Procurement',
  'Marketing & Communication',
  'Commercial',
  'HSEQ',
  'Sustainability',
] as const;

const VALUE_OPTIONS = [
  'Cost Savings',
  'Customer Experience',
  'Employee Satisfaction',
  'Operational Efficiency',
  'Risk & Compliance Management',
  'Revenue Generation',
  'Sustainability',
  'Other',
] as const;
/** UI label → backend BusinessDomain enum */
const DOMAIN_TO_API: Record<string, string> = {
  Cleaning: 'CLEANING',
  Food: 'FOOD',
  'Technical Services': 'TECHNICAL_SERVICES',
  Workplace: 'WORKPLACE',
  'P&C': 'P_AND_C',
  'IT&D': 'IT_AND_DIGITAL',
  Finance: 'FINANCE',
  'Legal & Procurement': 'LEGAL_AND_PROCUREMENT',
  'Marketing & Communication': 'MARKETING_AND_COMMUNICATION',
  Commercial: 'COMMERCIAL',
  HSEQ: 'HSEQ',
  Sustainability: 'SUSTAINABILITY',
};

/** UI label → backend ValueCreationType enum */
const VALUE_TO_API: Record<string, string> = {
  'Cost Savings': 'COST_SAVINGS',
  'Customer Experience': 'CUSTOMER_EXPERIENCE',
  'Employee Satisfaction': 'EMPLOYEE_SATISFACTION',
  'Operational Efficiency': 'OPERATIONAL_EFFICIENCY',
  'Risk & Compliance Management': 'RISK_AND_COMPLIANCE_MANAGEMENT',
  'Revenue Generation': 'REVENUE_GENERATION',
  Sustainability: 'SUSTAINABILITY',
  Other: 'OTHER',
};

type ValueOption = (typeof VALUE_OPTIONS)[number];

const BusinessJustificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [title, setTitle] = useState('');
  const [businessDomain, setBusinessDomain] = useState('');
  const [abstractText, setAbstractText] = useState('');
  const [motivation, setMotivation] = useState('');
  const [initiationSource, setInitiationSource] = useState<InitiationSource | null>(null);
  const [expectedValue, setExpectedValue] = useState<ValueOption[]>([]);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const toggleValue = (option: ValueOption) => {
    setExpectedValue((current) => {
      if (current.includes(option)) {
        return current.filter((item) => item !== option);
      }
      if (current.length >= 2) {
        return current;
      }
      return [...current, option];
    });
  };

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = 'Title is required.';
    if (!businessDomain) next.businessDomain = 'Business domain is required.';
    if (!abstractText.trim()) next.abstractText = 'Abstract is required.';
    if (!motivation.trim()) next.motivation = 'Motivation & reasoning is required.';
    if (!initiationSource) next.initiationSource = 'Select an initiation source.';
    if (expectedValue.length < 1) {
      next.expectedValue = 'Select at least one expected value (max 2).';
    }
    return next;
  }, [title, businessDomain, abstractText, motivation, initiationSource, expectedValue]);

  const isValid = Object.keys(errors).length === 0;

  if (!state.pocType) {
    return (
      <InitiationShell activeStep="step2">
        <Section theme="sand" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Business justification
          </Headline>
          <Lead>Choose a PoC type first (Step 1).</Lead>
          <button
            type="button"
            className="initiation__next"
            onClick={() => navigate('/pocs/new', { replace: true })}
          >
            Go to Step 1
          </button>
        </Section>
      </InitiationShell>
    );
  }

  const handleNext = async () => {
    setAttemptedSubmit(true);
    setSubmitError(null);
    if (!isValid) return;

    if (!pocId) {
      setSubmitError('Missing PoC ID. Go back to Step 1 and create the draft again.');
      return;
    }

    const domainCode = DOMAIN_TO_API[businessDomain];
    if (!domainCode) {
      setSubmitError('Unknown business domain.');
      return;
    }

    const valueCreation = expectedValue.map((v) => VALUE_TO_API[v]);
    if (valueCreation.some((v) => !v)) {
      setSubmitError('Unknown value creation type.');
      return;
    }
    const abstract = abstractText.trim();
    setIsSubmitting(true);
    try {
      await apiService.saveBusinessJustification(pocId, {
        title: title.trim(),
        businessDomain: domainCode,
        abstractDescription: abstract,
        abstractObjectives: abstract,
        abstractOutcomes: abstract,
        abstractEvidenceCriteria: abstract,
        motivation: motivation.trim(),
        initiationSource: initiationSource!,
        valueCreation,
      });

      navigate('/pocs/new/scorecard', {
        state: {
          pocId,
          pocType: state.pocType ?? 'EXTERNAL_SUPPLIER',
          businessJustification: {
            title: title.trim(),
            businessDomain,
            abstractText: abstract,
            motivation: motivation.trim(),
            initiationSource,
            expectedValue,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save business justification. Is the backend running on port 8080?'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <InitiationShell activeStep="step2">
      <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Business justification
          </Headline>
          <Lead>
            Step 2 — Complete the PoC details.
            {state.pocType === 'EXTERNAL_SUPPLIER'
              ? ' (External supplier)'
              : state.pocType
                ? ` (${state.pocType})`
                : ''}
          </Lead>

          <p className="initiation__note">
            Please note: You need to complete the forms for the whole Initiation
            phase in one session before submitting the entry.
          </p>

          <form
            className="bj-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleNext();
            }}
          >
            <label className="bj-field">
              <span className="bj-field__label">Title</span>
              <input
                className="bj-field__input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="PoC title"
              />
              {attemptedSubmit && errors.title ? (
                <span className="bj-field__error">{errors.title}</span>
              ) : null}
            </label>

            <label className="bj-field">
              <span className="bj-field__label">
                Which Business Domain are you referring to?
              </span>
              <select
                className="bj-field__input"
                value={businessDomain}
                onChange={(e) => setBusinessDomain(e.target.value)}
              >
                <option value="">Find items…</option>
                {BUSINESS_DOMAINS.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
              {attemptedSubmit && errors.businessDomain ? (
                <span className="bj-field__error">{errors.businessDomain}</span>
              ) : null}
            </label>

            <label className="bj-field">
              <span className="bj-field__label">
                Abstract — describe the PoC, objectives, targeted outcomes, and
                evidence of success
              </span>
              <textarea
                className="bj-field__input bj-field__input--area"
                rows={5}
                value={abstractText}
                onChange={(e) => setAbstractText(e.target.value)}
              />
              {attemptedSubmit && errors.abstractText ? (
                <span className="bj-field__error">{errors.abstractText}</span>
              ) : null}
            </label>

            <label className="bj-field">
              <span className="bj-field__label">
                Motivation &amp; reasoning — specific use case(s) this PoC aims to
                solve
              </span>
              <textarea
                className="bj-field__input bj-field__input--area"
                rows={4}
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
              />
              {attemptedSubmit && errors.motivation ? (
                <span className="bj-field__error">{errors.motivation}</span>
              ) : null}
            </label>

            <fieldset className="bj-field">
              <legend className="bj-field__label">
                What is the initiation source for this PoC?
              </legend>
              <div className="bj-choice-list">
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="initiationSource"
                    checked={initiationSource === 'INTERNAL'}
                    onChange={() => setInitiationSource('INTERNAL')}
                  />
                  <span>
                    <strong>Internal Trigger</strong>
                    <small>
                      Idea originated within the organisation (business challenge,
                      new product/service exploration).
                    </small>
                  </span>
                </label>
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="initiationSource"
                    checked={initiationSource === 'EXTERNAL'}
                    onChange={() => setInitiationSource('EXTERNAL')}
                  />
                  <span>
                    <strong>External Trigger</strong>
                    <small>
                      Driven by external factors (key account request, competitor
                      or market trend).
                    </small>
                  </span>
                </label>
              </div>
              {attemptedSubmit && errors.initiationSource ? (
                <span className="bj-field__error">{errors.initiationSource}</span>
              ) : null}
            </fieldset>

            <fieldset className="bj-field">
              <legend className="bj-field__label">
                Value creation — choose up to two expected benefits
              </legend>
              <div className="bj-value-grid">
                {VALUE_OPTIONS.map((option) => {
                  const checked = expectedValue.includes(option);
                  const atLimit = expectedValue.length >= 2 && !checked;
                  return (
                    <label
                      key={option}
                      className={
                        'bj-value' +
                        (checked ? ' bj-value--checked' : '') +
                        (atLimit ? ' bj-value--disabled' : '')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={atLimit}
                        onChange={() => toggleValue(option)}
                      />
                      {option}
                    </label>
                  );
                })}
              </div>
              {attemptedSubmit && errors.expectedValue ? (
                <span className="bj-field__error">{errors.expectedValue}</span>
              ) : null}
            </fieldset>

            {submitError ? (<p className="bj-field__error">{submitError}</p>) : null}

            <div className="initiation__actions">
              <button
                type="button"
                className="initiation__link-back"
                onClick={() =>
                  navigate('/pocs/new', {
                    state: { pocId, pocType: state.pocType },
                  })
                }
              >
                ← Back to Step 1
              </button>
              <button type="submit" 
              className="initiation__next"
              disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : 'Next →'}
                
              </button>
            </div>
          </form>
        </Section>
    </InitiationShell>
  );
};

export default BusinessJustificationPage;
